import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { invalidateAfter, keys } from "../api/query-registry";
import type { ResidentItem, UpdateResident } from "./useResidents";
import type { StatusHistoryEntry } from "./useStatusHistory";

// Server-side detail = list item + paper-form placement + work-window flag.
export interface PaperSlot {
  half: "first" | "second";
  month: string;
  label: string;
}

export interface IcpHistoryEntry {
  id: string;
  icpDate: string; // YYYY-MM-DD — the staff-written ICP date
  frequencyAfter: string | null;
}

export interface ResidentDetail extends ResidentItem {
  paperSlot: PaperSlot | null;
  workWindowToday: boolean;
  statusHistory: StatusHistoryEntry[];
  icpHistory: IcpHistoryEntry[];
}

export interface IcpCompleteBody {
  stage: "ax" | "fill";
  /** The ICP date written by staff — required for "fill"; the next deadline is anchored to it. */
  icpDate?: string;
  completeLinkedTodos?: boolean;
}

export function useResidentDetail(id: string | null) {
  return useQuery({
    queryKey: keys.resident(id),
    queryFn: () => api<ResidentDetail>(`/api/residents/${id}`),
    enabled: id !== null,
    staleTime: 30_000,
  });
}

/** The two server-side ICP state transitions plus the plain resident PATCH the
 *  guided flow still uses for the intermediate "form available" step. */
export function useIcpActions(id: string) {
  const qc = useQueryClient();
  const invalidate = () => invalidateAfter(qc, "residentChanged");
  const patch = useMutation({
    mutationFn: (body: UpdateResident) => api<ResidentItem & { todosCreated?: number }>(`/api/residents/${id}`, { method: "PATCH", body }),
    onSuccess: invalidate,
  });
  const complete = useMutation({
    mutationFn: (body: IcpCompleteBody) =>
      api<{ resident: ResidentDetail; completedTodoIds: string[] }>(`/api/residents/${id}/icp/complete`, { method: "POST", body }),
    onSuccess: invalidate,
  });
  return { patch, complete };
}
